import { computeTablePricing } from '@proposify/pricing-engine';
import type { ResolvedTablePricingInputs } from '@proposify/pricing-engine';
import { ColumnTypeIdentifier } from '../../../../grid/reduxStore/table.types';
import type { TableColumnType, TableRowType, TableType } from '../../../../grid/reduxStore/table.types';
import type { Currency } from '../../../../grid/reduxStore/documentSettingsSlice';
import { normalizeRowCellValues } from '../tableHelpers';
import { toEngineRows, toEngineColumns, hasPricingInput } from './tableEngineMapper';
import type { UpdatedTableTotals } from './getUpdatedTableTotals';
import { buildUpdatedTableTotals, getUpdatedTableTotals } from './getUpdatedTableTotals';

// Single builder for engine inputs, shared by the render path (pricingSelectors)
// and the persistence path (getUpdatedTableTotalsFromEngine). Any divergence
// between the two means the editor displays one total and persists another —
// keep this the only place engine inputs are assembled from table state.
export function buildEnginePricingInputs(
  rows: TableRowType[],
  columns: TableColumnType[],
  metadata: TableType['metadata'] | undefined,
  currencySettings: Currency | null,
  discounts: TableType['discounts']
): ResolvedTablePricingInputs {
  const currency: ResolvedTablePricingInputs['currency'] = {
    code: currencySettings?.code || 'USD',
    symbol: currencySettings?.symbol || '$',
    symbolPosition: currencySettings?.symbolPosition || 'front',
    decimalSeparator: currencySettings?.decimalSeparator || '.',
    thousandsSeparator: currencySettings?.thousandsSeparator || ',',
    decimalPlaces: currencySettings?.decimalPlaces || 2,
    roundingIncrement: '0.01',
  };
  return {
    rows: toEngineRows(
      rows.map((row) => normalizeRowCellValues(row, columns)),
      columns
    ),
    columns: toEngineColumns(columns),
    currency,
    settings: {
      precision: metadata?.pricingDecimalPlaces || 5,
      roundingMode: 'nearest',
      currency,
    },
    discounts: discounts || [],
  };
}

export function getUpdatedTableTotalsFromEngine(
  rows: TableRowType[],
  columns: TableColumnType[],
  tableData: TableType,
  currencySettings: Currency | null
): UpdatedTableTotals {
  try {
    const inputs = buildEnginePricingInputs(rows, columns, tableData.metadata, currencySettings, tableData.discounts);
    const result = computeTablePricing(inputs);
    const subtotalColumn = columns.find((column) => column.columnType === ColumnTypeIdentifier.SUBTOTAL);
    // inputs.rows is a 1:1 map over `rows` (see buildEnginePricingInputs), so
    // inputs.rows[index] is rows[index]'s own engine counterpart.
    const updatedRows = subtotalColumn
      ? rows.map((row, index) => {
          const engineRow = inputs.rows[index];
          const rowResult = result.rows[row.id];
          if (!rowResult || !hasPricingInput(engineRow, inputs.columns)) return row;
          return { ...row, [subtotalColumn.field]: rowResult.discountedSubtotal };
        })
      : rows;
    // RISK: metadata.tableTotal and totalRows math are number-typed on the wire —
    // the engine's decimal string is coerced here. Precision is already applied by
    // the engine; fixing the wire type is a breaking API change tracked separately.
    return { ...buildUpdatedTableTotals(Number(result.grandTotal), tableData), rows: updatedRows };
  } catch (error) {
    // Never block a save on an engine failure — fall back to the legacy sum so the
    // snapshot stays internally consistent with pre-engine behaviour.
    console.error('Pricing calculation error, falling back to legacy totals:', error);
    return getUpdatedTableTotals(rows, columns, tableData);
  }
}
